<template>
  <SoAdminPage
    title="EV Smart Charging"
    subtitle="So EV Connected eligibility, waitlist and onboarded accounts"
  >
    <template v-if="isPageLoading">
      <SoSkeleton width="100%" height="150px" class="mt-8 mb-10" />
      <SoSkeleton width="240px" height="28px" class="mb-4" />
      <SoSkeleton width="100%" height="420px" />
    </template>

    <template v-else>
      <SoRadioCards class="mt-8 mb-10" :mandatory="false">
        <SoRadioCardChart
          v-for="card in cards"
          :key="card.name"
          :title="card.label"
          :value="card.name"
          :percentage="card.ring"
          :type="card.type"
          :data-testid="`smart-charging-kpi-${card.name}`"
        >
          <span class="text-h2">{{ card.text }}</span>
          <button
            v-if="card.name === 'eligibilityChecks'"
            type="button"
            class="d-block text-body-2 text-primary mt-1 device-breakdown-link"
            data-testid="smart-charging-device-breakdown-link"
            @click.stop="showDeviceModal = true"
          >
            View device breakdown
          </button>
        </SoRadioCardChart>
      </SoRadioCards>

      <SoDivider class="mb-6" />

      <h2 class="text-h5 mb-4" data-testid="smart-charging-accounts-title">
        Smart Charging accounts
      </h2>

      <SoAlert
        v-if="accountsQuery.error"
        title="Failed to load smart charging accounts"
        data-testid="smart-charging-accounts-error"
      />
      <SoDataTable
        v-else
        class="smart-charging-accounts-table"
        :headers="accountsTableHeaders"
        :items="tableItems"
        :loading="accountsQuery.isFetching"
        searchable
        search-placeholder="Search account number"
        data-testid="smart-charging-accounts-table"
      >
        <template #tableActions>
          <SoToggle
            v-model="showOffboarded"
            label="Show offboarded"
            data-testid="smart-charging-show-offboarded"
          />
        </template>

        <template #itemActions="{ item }">
          <SoButton
            type="table-action"
            title="Open in Nova"
            :disabled="!item.accountNumber"
            data-testid="smart-charging-open-in-nova"
            @click="openInNova(item)"
          />
        </template>
      </SoDataTable>
    </template>

    <SmartChargingDeviceBreakdownDialog
      v-model="showDeviceModal"
      :chargers="eligibilityQuery.data.chargers"
      :evs="eligibilityQuery.data.evs"
      :loading="eligibilityQuery.isFetching"
    />
  </SoAdminPage>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoRadioCards from "@/components/SoRadioCards.vue"
  import SoRadioCardChart from "@/components/SoRadioCardChart.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SmartChargingDeviceBreakdownDialog from "./SmartChargingDeviceBreakdownDialog.vue"
  import { RouteNames } from "@/types/router"
  import { QueryResult } from "@/api/utils/types"
  import { SmartChargingService } from "@/services/operations/SmartChargingService"
  import { AccountService } from "@/services/AccountService"
  import { MeterpointService } from "@/services/MeterpointService"
  import { ISmartChargingAccount } from "@/models/operations/smartCharging/smartChargingAccount/smartChargingAccount.types"
  import { SoDataTableItem } from "@/types/components/SoDataTable"
  import { accountsTableHeaders, kpiCards } from "./opsSmartChargingConfig"

  const percentage = (value: number, total: number) =>
    total ? Math.round((value / total) * 100) : 0

  export default defineComponent({
    components: {
      SoAdminPage,
      SoRadioCards,
      SoRadioCardChart,
      SoSkeleton,
      SoDataTable,
      SoButton,
      SoToggle,
      SoDivider,
      SoAlert,
      SmartChargingDeviceBreakdownDialog,
    },
    data() {
      return {
        RouteNames,
        accountsTableHeaders,
        showOffboarded: false,
        showDeviceModal: false,
        waitlistQuery: SmartChargingService.getWaitlistAccountIds(),
        eligibilityQuery: SmartChargingService.getEligibilityBreakdown(),
        accountsQuery: SmartChargingService.getAccounts(),
        accountNumbersQuery: null as QueryResult<Record<number, string>> | null,
        meterPointsQuery: null as QueryResult<Record<number, string>> | null,
      }
    },
    computed: {
      isPageLoading(): boolean {
        return (
          this.waitlistQuery.isLoading ||
          this.eligibilityQuery.isLoading ||
          this.accountsQuery.isLoading
        )
      },
      cards() {
        const eligibility = this.eligibilityQuery.data
        const waitlistIds = this.waitlistQuery.data

        // A waitlist account is "compatible" when its latest eligibility check
        // is eligible — the same account-level definition as the checks card.
        const compatibleAccounts = new Set(eligibility.compatibleAccountIds)
        const waitlistTotal = waitlistIds.length
        const waitlistCompatible = waitlistIds.filter((id) =>
          compatibleAccounts.has(id),
        ).length

        const values: Record<string, { text: string; ring: number }> = {
          eligibilityChecks: {
            text: String(eligibility.total),
            ring: 100,
          },
          waitlist: {
            text: String(waitlistTotal),
            ring: 100,
          },
          compatibleChecks: (() => {
            const value = percentage(eligibility.compatible, eligibility.total)
            return {
              text: `${eligibility.compatible} (${value}%)`,
              ring: value,
            }
          })(),
          compatibleWaitlist: (() => {
            const value = percentage(waitlistCompatible, waitlistTotal)
            return {
              text: `${waitlistCompatible} (${value}%)`,
              ring: value,
            }
          })(),
        }
        return kpiCards.map((card) => ({ ...card, ...values[card.name] }))
      },
      accountNumberMap(): Record<number, string> {
        return this.accountNumbersQuery?.data ?? {}
      },
      meterPointIdentifierMap(): Record<number, string> {
        return this.meterPointsQuery?.data ?? {}
      },
      tableItems(): SoDataTableItem[] {
        return (this.accountsQuery.data ?? [])
          .filter((account) => this.showOffboarded || !account.offboardedAt)
          .map((account) => ({
            ...account,
            accountNumber:
              this.accountNumberMap[account.billingAccountId] ?? null,
            meterPointIdentifier:
              account.meterPointId === null
                ? null
                : (this.meterPointIdentifierMap[account.meterPointId] ?? null),
          }))
      },
    },
    watch: {
      "accountsQuery.data": {
        handler(accounts: ISmartChargingAccount[]) {
          const rows = accounts ?? []

          const billingAccountIds = [
            ...new Set(rows.map((a) => a.billingAccountId)),
          ]
          if (billingAccountIds.length) {
            this.accountNumbersQuery =
              AccountService.getAccountNumberMap(billingAccountIds)
          }

          const meterPointIds = [
            ...new Set(
              rows
                .map((a) => a.meterPointId)
                .filter((id): id is number => id !== null),
            ),
          ]
          if (meterPointIds.length) {
            this.meterPointsQuery =
              MeterpointService.getMeterPointIdentifierMap(meterPointIds)
          }
        },
        immediate: true,
      },
    },
    methods: {
      openInNova(item: SoDataTableItem) {
        const accountNumber = item.accountNumber as string | null
        if (!accountNumber) return
        const route = this.$router.resolve({
          name: RouteNames.CustomerOverview,
          params: { accountNumber },
        })
        window.open(route.href, "_blank")
      },
    },
  })
</script>

<style scoped>
  .device-breakdown-link {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
  }
  /* Cap the Axle site ID column (2nd) so long ids don't stretch the table. */
  .smart-charging-accounts-table :deep(td:nth-child(2)),
  .smart-charging-accounts-table :deep(th:nth-child(2)) {
    max-width: 120px;
    overflow-wrap: break-word;
  }
</style>
